import { addDays, subDays } from 'date-fns';

import { parseStringToDate } from '@utils/kpi-utils/date';

import { Task } from '@interfaces/ganttTasks/ganttTasks';
import { IPortalTimelineMilestoneWithTasks, IPortalTimelineTask } from '@interfaces/portal/timeline';

import { EGanttTaskType } from '@enums/ganttTasks';

import { linkedTasksStatusNames } from '@constants/common';
import { TASKS_WITHOUT_MILESTONES_ROW } from '@components/common/Gantt/constants';

export const prepareGanttChartData = (
	portalTimelineMilestonesWithTasks: IPortalTimelineMilestoneWithTasks[],
	portalTimelineTasksWithoutMilestones: IPortalTimelineTask[],
): Task[] => {
	const tasks: Task[] = [];

	const tasksWithoutMilestoneRow: Task = {
		id: TASKS_WITHOUT_MILESTONES_ROW,
		name: 'Tasks without milestone',
		type: EGanttTaskType.milestone,
		start: new Date(),
		end: new Date(),
		isWithoutStartDate: true,
		isWithoutEndDate: true,
		hideChildren: false,
	};

	for (let i = 0; i < portalTimelineMilestonesWithTasks.length; i++) {
		const currentValue = portalTimelineMilestonesWithTasks[i];

		const milestone: Task = {
			id: currentValue.id,
			name: currentValue.title,
			type: EGanttTaskType.milestone,
			start: parseStringToDate(currentValue.startDate) || new Date(),
			end: parseStringToDate(currentValue.endDate) || new Date(),
			hideChildren: false,
			status: currentValue.status,
		};

		tasks.push(milestone);

		for (let j = 0; j < currentValue.tasks.length; j++) {
			const task = currentValue.tasks[j];

			const startDate = task?.startDate && parseStringToDate(task.startDate);
			const endDate = task?.endDate && parseStringToDate(task.endDate);

			const milestoneTask: Task = {
				id: task.id,
				name: `${task.taskId} ${task.title}`,
				type: EGanttTaskType.task,
				status: task.status,
				milestone: currentValue.id,
				priority: task.priority,
				assignees: task.assignees,
				start: startDate || (endDate ? subDays(endDate, 1) : new Date()),
				end: endDate || (startDate ? addDays(startDate, 1) : addDays(new Date(), 1)),
				isWithoutStartDate: !startDate,
				isWithoutEndDate: !endDate,
				dependencies: task.linkedTasks
					?.filter(linkedTask => linkedTask.isPredecessor)
					?.map(linkedTask => {
						return {
							id: linkedTask.id,
							type: linkedTask.type.name,
							isActive: linkedTask.status.name === linkedTasksStatusNames.active,
						};
					}),
			};

			tasks.push(milestoneTask);
		}
	}

	tasks.push(tasksWithoutMilestoneRow);

	for (let i = 0; i < portalTimelineTasksWithoutMilestones.length; i++) {
		const task = portalTimelineTasksWithoutMilestones[i];

		const startDate = task?.startDate && parseStringToDate(task.startDate);
		const endDate = task?.endDate && parseStringToDate(task.endDate);

		const taskData: Task = {
			id: task.id,
			name: `${task.taskId} ${task.title}`,
			type: EGanttTaskType.task,
			status: task.status,
			priority: task.priority,
			assignees: task.assignees,
			start: startDate || (endDate ? subDays(endDate, 1) : new Date()),
			end: endDate || (startDate ? addDays(startDate, 1) : addDays(new Date(), 1)),
			isWithoutStartDate: !startDate,
			isWithoutEndDate: !endDate,
			milestone: TASKS_WITHOUT_MILESTONES_ROW,
			dependencies: task.linkedTasks
				?.filter(linkedTask => linkedTask.isPredecessor)
				?.map(linkedTask => {
					return {
						id: linkedTask.id,
						type: linkedTask.type.name,
						isActive: linkedTask.status.name === linkedTasksStatusNames.active,
					};
				}),
		};

		tasks.push(taskData);
	}

	return tasks;
};
